/**
 * マスター 5 型の入力スキーマ（Actor / Domain / Stage / Verb / Unit）。
 *
 * どれも件数がほぼ固定で、書く入口は一般の書込ではなくマスターの管理である
 * （`MASTER_NODE_TYPES`。NE §1-subsets）。ほかの型がこれらを参照するので、書く順序の
 * 先頭に来る（NE §5）。
 *
 * **自由文のラベルを持つのは `Verb` と `Unit` だけである**（NE §1-label）。ほかの 3 型は
 * ラベルを語の組み合わせから導出する（`labels.ts`）ので、ラベルの欄そのものを持たない。
 * 動詞と単位が自由文なのは、組み立てる材料が自分より下に無いためである。
 *
 * **説明を持つのは `Actor` / `Domain` / `Stage` だけである**（ほかには
 * 用語）。承認制で件数が増えず、書き手が人に限られる欄だけに自由文を残す（NE §1-attrs）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 作り直しのマスター 5 型（Actor / Domain / Stage / Verb / Unit）の zod 入力スキーマ
 * @graph-connects none
 */
import { z } from 'zod';
import { localizedText, nonEmptyString, VERB_LAYERS } from '../node-schemas.js';
import { optionalSpecElementId, specElementId } from './ids.js';
/**
 * 単位の表し方 3 種（NE §1-attrs の Unit）。
 *
 * 率は小数（`0.1`）で始める。整数と小数を分けるのは、判定表のセルや式の項に書かれた数を
 * 読むときに「1 と書いてあるのは 1 件か 100% か」を決めるためである。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 単位の表し方（整数 / 小数 / 日時）の allowlist
 * @graph-connects none
 */
export const UNIT_NOTATIONS = ['integer', 'decimal', 'dateTime'];
/**
 * 動詞（Verb）。今の `Term(kind="verb")` の 4 欄をそのまま持ち越す（決定。2026-09-21）。
 *
 * 持ち越すのは、動詞の層（`layer`）が業務の帰結と合うかを見る制約と、業務名の言い換えを
 * 拒否する仕組み（`rejectedSynonyms`）がこの 4 欄に依存しているためである。用語から出して
 * 独立の型にしたのは、動詞が「語の定義」ではなく**名前を組み立てる材料**だからである。
 *
 * `nounForm`（名詞形）は必須である。ステージの名前は「クラス ＋ 動詞の名詞形」で組むので
 * （アイテム選定）、名詞形が無い動詞をステージに使うと名前が組めない。どの動詞がステージに
 * 使われるかは書く時点で決まらないので、全部の動詞に持たせる。
 *
 * `rejectedSynonyms` は任意である。値が入るのは、業務名に書かれたら拒否したい言い換えが
 * 見つかったときで、動詞を立てた時点では分からない（「受け付ける」に対する「受理する」は、
 * 誰かがそう書いて初めて分かる）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 動詞（Verb）の zod 入力スキーマ。辞書形・完了形・到達状態の層と内容・名詞形・受け付けない言い換え
 * @graph-connects none
 */
export const verbElementSchema = z.strictObject({
    id: optionalSpecElementId(),
    label: localizedText(),
    completedForm: localizedText(),
    layer: z.enum(VERB_LAYERS),
    reachedState: localizedText(),
    nounForm: localizedText(),
    rejectedSynonyms: z.array(nonEmptyString()).optional(),
});
/**
 * 単位（Unit）。円 / 率 / 日 / 日時 / 件 の 5 件がここに入る。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 単位（Unit）の zod 入力スキーマ。ラベルと表し方
 * @graph-connects none
 */
export const unitElementSchema = z.strictObject({
    id: optionalSpecElementId(),
    label: localizedText(),
    notation: z.enum(UNIT_NOTATIONS),
});
/**
 * アクター（Actor）。業務を行うロール。
 *
 * ラベルは「クラス（＋ 限定する語）」で組む（スタイリスト（上長））。`subject` はクラスの
 * 用語、`qualifier` は限定するクラスの用語で、限定が要らないロールでは値が入らない。
 * クラスとしてのスタイリストとロールとしてのスタイリストは別物として残す。
 *
 * `icon` は任意である。値が入るのは画面にアイコンを出すロールだけで、内部の主体（system）
 * には無い。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business アクター（Actor）の zod 入力スキーマ。名前の作り・説明・アイコン
 * @graph-connects none
 */
export const actorElementSchema = z.strictObject({
    id: optionalSpecElementId(),
    subject: specElementId(),
    qualifier: specElementId().optional(),
    description: localizedText(),
    icon: nonEmptyString().optional(),
});
/**
 * ドメイン（Domain）。業務の束ね。
 *
 * ラベルはクラスの用語 1 語で組む（スタイリング）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business ドメイン（Domain）の zod 入力スキーマ。名前の作りと説明
 * @graph-connects none
 */
export const domainElementSchema = z.strictObject({
    id: optionalSpecElementId(),
    subject: specElementId(),
    description: localizedText(),
});
/**
 * ステージの出口。業務 1 件と、その業務の結末の行 1 つの組。
 *
 * 結末の行は局所 id を持たず、結果の値の用語 id が行の鍵である。したがって `ending` は
 * 結果の値の用語 id で、業務の結末・フローの枝と同じ指し方をする。指した行が実在するかは
 * グラフ全体を見ないと分からないので、ここでは形だけを見る（制約 61）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business ステージの出口（業務と、その結末の行の結果の値）の zod 入力スキーマ
 * @graph-connects none
 */
export const stageExitSchema = z.strictObject({
    business: specElementId(),
    ending: specElementId(),
});
/**
 * ステージの入口。前のステージの出口と同じ結末の行（業務 1 件と、その業務の結末の行 1 つの組）。
 *
 * 分岐のステージは、前のステージの出口の業務の別の結末の行を指す。形は出口と同じで、指した行が
 * 実在するか・その業務を出口に持つステージが在るかはグラフ全体を見ないと分からないので、ここでは
 * 形だけを見る（制約 70）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business ステージの入口（前のステージの出口と同じ業務の結末の行）の zod 入力スキーマ
 * @graph-connects none
 */
export const stageEntrySchema = stageExitSchema;
/**
 * ステージ（Stage）。入口から出口まで（区切りから区切りまで）。
 *
 * ラベルは「クラス ＋ 動詞の名詞形」で組む（アイテム選定）。順番の欄は持たない — ステージの並び
 * （鎖）は入口の参照から導出する（`deriveStageChains`。NE §1）。**入口を持たないものが最初の
 * ステージ、入口も出口も持たないものが横断ステージ**である。
 *
 * 入口と出口は業務の結末を指すので、業務より先に書くマスターの段では埋められない — ステージを
 * 入口・出口なしで立て、業務を書いたあとに埋める（NE §5）。出口が在るか・入口の参照先が在るかは
 * リリースゲートが見る（制約 61・70）。
 *
 * 逆に、入口だけを持ち出口を持たない形は書込で止める。入口を持つステージは鎖の中に居るので
 * 横断ステージではなく、出口を持つものとして定義されていて（NE §0-grain）、1 件の入力だけで
 * 食い違いが決まるからである。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business ステージ（Stage）の zod 入力スキーマ。名前の作り・入口・出口・説明
 * @graph-connects none
 */
export const stageElementSchema = z
    .strictObject({
    id: optionalSpecElementId(),
    subject: specElementId(),
    verb: specElementId(),
    entry: stageEntrySchema.optional(),
    exit: stageExitSchema.optional(),
    description: localizedText(),
})
    .refine((stage) => stage.entry === undefined || stage.exit !== undefined, {
    message: '入口（entry）を持つステージは出口（exit）も持つ',
    path: ['exit'],
});
/**
 * 復元した表示名（ja / en / vi）。**移行の間だけ持つ欄で、人が戻す欄が埋まったら消す。**
 *
 * 業務フローのラベルは「ステージ ＋ 状況」で組む（`labels.ts`）。旧データの復元はその材料を
 * **未設定のまま**戻すので（`RESTORE_DEFERRED_FIELDS`）、埋まるまでのあいだ名前が 1 つも
 * 出ない。いっぽう退避表には旧いストーリー名の文字列が 3 言語ぶん残っている。そこを繋ぐのが
 * この欄である。
 *
 * **導出の規則は変えない。** ラベルを引く側は材料が揃えば必ず導出を返し、**揃わないときだけ**
 * この欄を返す。つまり人が材料を埋めた瞬間に、この欄は誰からも読まれなくなる。読まれなく
 * なってから消すので、消す時期は「業務フロー 29 本の材料が全部埋まったとき」である。
 *
 * `sourceRules`（処理の元になった規則）と同じ立場の欄である — どちらも退避表にしか無い事実を
 * 移行の間だけ運び、復元が終わったら欄ごと消す。
 *
 * **業務フローだけが使う。** ステージは復元で写さず人が立てるので名前の材料が
 * 最初から揃い、業務・画面・部品・アクター・ドメインは材料の一部か全部が機械で戻っている。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 復元した表示名（移行の間だけ持ち、人が戻す欄が埋まったら消す）の zod 入力スキーマ
 * @graph-connects none
 */
export const restoredLabelSchema = localizedText();
//# sourceMappingURL=masters.js.map